import { useState } from "react";
import { startOfWeek } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useGetDispatchCenters,
  type DispatchCenterListItem,
} from "@/api/dispatch-centers";
import { filterAndSortCentersForCarriers } from "@/components/dispatch-center-filter";
import { WeekNavigator } from "./week-navigator";
import { SchedulingGrid } from "./scheduling-grid";
import { RequestAvailabilityCard } from "./request-availability-card";
import { Loader2 } from "lucide-react";
import { useCarrierScope } from "@/contexts/carrier-scope-context";

export function SchedulingPage() {
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedDispatchCenterId, setSelectedDispatchCenterId] =
    useState<string>("");
  const {
    carrierIds: scopedCarrierIds,
    dispatchCenterIds: scopedDispatchCenterIds,
  } = useCarrierScope();

  const { data: centers, isLoading: centersLoading } = useGetDispatchCenters();

  // Narrow the picker by the top-bar carrier + center scope, matching the
  // prior depot-dropdown behaviour.
  const visibleCenters: DispatchCenterListItem[] =
    filterAndSortCentersForCarriers(centers ?? [], scopedCarrierIds).filter(
      (c) =>
        scopedDispatchCenterIds.length === 0 ||
        scopedDispatchCenterIds.includes(c.id)
    );

  // Auto-select first center. Also reset when the current center is no longer
  // in the scoped list (e.g. carrier picker changed underneath us).
  const inList = visibleCenters.some((c) => c.id === selectedDispatchCenterId);
  if (visibleCenters.length > 0 && !inList) {
    setSelectedDispatchCenterId(visibleCenters[0].id);
  } else if (visibleCenters.length === 0 && selectedDispatchCenterId) {
    setSelectedDispatchCenterId("");
  }

  if (centersLoading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold">Scheduling</h1>
        <div className="flex items-center gap-4">
          <Select
            value={selectedDispatchCenterId}
            onValueChange={setSelectedDispatchCenterId}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select dispatch center" />
            </SelectTrigger>
            <SelectContent>
              {visibleCenters.map((center) => (
                <SelectItem key={center.id} value={center.id}>
                  {center.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <WeekNavigator weekStart={weekStart} onChange={setWeekStart} />
        </div>
      </div>

      {!selectedDispatchCenterId ? (
        <div className="text-center py-12 text-gray-500">
          Select a dispatch center to view scheduling
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Sidebar - Request Availability */}
          <div className="w-full lg:w-72 shrink-0">
            <RequestAvailabilityCard
              weekStart={weekStart}
              dispatchCenterId={selectedDispatchCenterId}
            />
          </div>

          {/* Main Grid - Unified Availability + Shifts */}
          <div className="flex-1 min-w-0 overflow-x-auto">
            <SchedulingGrid
              weekStart={weekStart}
              dispatchCenterId={selectedDispatchCenterId}
            />
          </div>
        </div>
      )}
    </div>
  );
}
